const cache = new Map<string | number, Date>();

export const getDateWithCache = (date: string | number): Date => {
  const cachedDate = cache.get(date);
  if (cachedDate) {
    return cachedDate;
  }
  if (cache.size > 200) {
    cache.clear();
  }
  const newDate = new Date(date);
  cache.set(date, newDate);
  return newDate;
};

export const niceTime = (timestamp: number | string): string => {
  return new Date(timestamp).toLocaleTimeString(navigator.language, { hour: 'numeric', minute: '2-digit' });
};

export function niceDateTimeTz(
  timestamp: number | string,
  langCode?: string,
  monthLen: 'long' | 'short' = 'long',
  showYear = true,
  showDayOfWeek = false,
  bulletAfterYear = false
): string {
  //if this is the US - replace the second comma generated by the locale date string with the word "at". We don't know what is the expected behavior outside the US - The local date string does not have a second comma, so removed the getPosition function in the original code to get rid of the extra "at" in the time string.
  if (navigator.language === 'en-US' && langCode && langCode !== 'en') {
    // if the user is in the US, but chooses a different default language, then override
    let str = new Date(timestamp).toLocaleString(langCode, {
      month: monthLen,
      day: '2-digit',
      year: showYear ? 'numeric' : undefined,
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
      weekday: showDayOfWeek ? 'short' : undefined,
    });
    if (bulletAfterYear) {
      str = str.replace(/,(?!.*,)/, '  •  ');
    }
    return str;
  } else {
    let str = new Date(timestamp).toLocaleString(navigator.language, {
      month: monthLen,
      day: '2-digit',
      year: showYear ? 'numeric' : undefined,
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
      weekday: showDayOfWeek ? 'short' : undefined,
    });
    if (bulletAfterYear) {
      str = str.replace(/,(?!.*,)/, '  •  ');
    }
    return str;
  }
}

const niceDateMemo: Map<string, string> = new Map();
export function niceDate(
  timestamp: number | string,
  langCode?: string,
  monthLen: 'long' | 'short' = 'long',
  showYear = true,
  showDayOfWeek = false,
  timeZone?: string
): string {
  const key = `${timestamp}|${langCode ?? ''}|${monthLen}|${showYear}|${showDayOfWeek}|${timeZone ?? ''}|${navigator.language}`;
  const cached = niceDateMemo.get(key);
  if (cached) return cached;

  //if this is the US - replace the second comma generated by the locale date string with the word "at". We don't know what is the expected behavior outside the US - The local date string does not have a second comma, so removed the getPosition function in the original code to get rid of the extra "at" in the time string.
  if (navigator.language === 'en-US' && langCode && langCode !== 'en') {
    // if the user is in the US, but chooses a different default language, then override
    const str = new Date(timestamp).toLocaleDateString(langCode, {
      month: monthLen,
      day: '2-digit',
      year: showYear ? 'numeric' : undefined,
      weekday: showDayOfWeek ? 'short' : undefined,
      timeZone,
    });
    niceDateMemo.set(key, str);
    return str;
  } else {
    const str = new Date(timestamp).toLocaleDateString(navigator.language, {
      month: monthLen,
      day: '2-digit',
      year: showYear ? 'numeric' : undefined,
      weekday: showDayOfWeek ? 'short' : undefined,
      timeZone,
    });
    niceDateMemo.set(key, str);
    return str;
  }
}

export const getUTCDateInfo = ({
  startTimestamp,
  endTimestamp,
}: {
  startTimestamp?: number | null;
  endTimestamp?: number | null;
}) => {
  const date = getDateWithCache(startTimestamp || '');
  // set timezone to UTC
  const startDateAsUTC = getDateWithCache(date.getTime() + date.getTimezoneOffset());
  const endDate = getDateWithCache(endTimestamp || '');
  const endDateAsUTC = getDateWithCache(endDate.getTime() + endDate.getTimezoneOffset());
  const fullUTCDate = startDateAsUTC.toLocaleString(undefined, {
    timeZone: 'UTC',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  });
  const utcMonth = startDateAsUTC.toLocaleString(undefined, { timeZone: 'UTC', month: 'short' });
  const utcDay = startDateAsUTC.toLocaleString(undefined, { timeZone: 'UTC', day: '2-digit' });
  return {
    date: startDateAsUTC,
    fullDate: fullUTCDate,
    month: utcMonth,
    day: utcDay,
    endDate: endDateAsUTC,
    timeZone: 'UTC',
    localStartDate: date,
    localEndDate: endDate,
  };
};

export const getLocalDateInfo = ({
  startTimestamp,
  endTimestamp,
  selectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  includeTimeInFullDate,
}: {
  startTimestamp?: number | null;
  endTimestamp?: number | null;
  selectedTimezone?: string;
  includeTimeInFullDate?: boolean;
}) => {
  let startDate: Date | null = null;
  let fullDate: string | null = null;
  let month: string | null = null;
  let day: string | null = null;
  let endDate: Date | null = null;

  const navigatorLang = window.navigator.language;

  if (startTimestamp) {
    startDate = getDateWithCache(startTimestamp || '');
    fullDate = startDate.toLocaleString(navigatorLang, {
      timeZone: selectedTimezone,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      ...(includeTimeInFullDate
        ? {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
          }
        : {}),
    });
    month = startDate.toLocaleString(navigatorLang, { timeZone: selectedTimezone, month: 'short' });
    day = startDate.toLocaleString(navigatorLang, { timeZone: selectedTimezone, day: '2-digit' });
  }

  if (endTimestamp) {
    endDate = getDateWithCache(endTimestamp || '');
  }

  return {
    date: startDate,
    fullDate,
    month,
    day,
    endDate,
    timeZone: selectedTimezone,
    localStartDate: startDate,
    localEndDate: endDate,
  };
};

export const formatMsToHMMSS = (ms: number) => {
  const seconds = Math.floor(ms / 1000);
  const hours = Math.floor(seconds / 3600);
  const hh = String(hours).padStart(2, '0');
  const mm = String(Math.floor((seconds / 60) % 60));
  const ss = String(seconds % 60).padStart(2, '0');
  if (hours) {
    return `${hh}:${mm}:${ss}`;
  }

  return `${mm}:${ss}`;
};

export const formatMinutesToHoursAndMinutes = (minutes: number): string => {
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

export const getNext30minTimestamp = (): number => {
  return Math.ceil(Date.now() / 1000 / 60 / 30) * 1000 * 60 * 30;
};

export const addMinutesToTimestamp = (timestamp: number, minutesToAdd: number): number => {
  return timestamp + 1000 * 60 * minutesToAdd;
};

export const formatMsToHHMMSS = (ms: number) => {
  const seconds = Math.floor(Math.max(0, ms) / 1000);
  const hours = Math.floor(seconds / 3600);
  const hh = String(hours).padStart(2, '0');
  const mm = String(Math.floor((seconds / 60) % 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  if (hours) {
    return `${hh}:${mm}:${ss}`;
  }

  return `${mm}:${ss}`;
};

export const durationBetweenTimestamps = (start: string | number, end: string | number): string => {
  const startDate = getDateWithCache(start);
  const endDate = getDateWithCache(end);
  const startTimestamp = startDate.valueOf();
  const endTimestamp = endDate.valueOf();
  const ms = endTimestamp - startTimestamp;
  return formatMsToHHMMSS(ms);
};

export interface TimeOfDayOption {
  value: string;
  label: string;
}

// Generates time-of-day dropdown options spanning a full day at a fixed interval, labelled in
// 12-hour format (e.g. "3:15 PM"). value === label so it drops straight into a select whose value
// is the formatted time string. intervalMinutes defaults to 15; pass 30, 60, etc. for coarser grids
// (must be a positive integer that divides the day cleanly).
export const getTimeOfDayOptions = (intervalMinutes = 15): TimeOfDayOption[] => {
  const options: TimeOfDayOption[] = [];
  const minutesPerDay = 24 * 60;
  const step = intervalMinutes > 0 ? intervalMinutes : 15;
  for (let minutes = 0; minutes < minutesPerDay; minutes += step) {
    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    const period = hour < 12 ? 'AM' : 'PM';
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    const label = `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
    options.push({ value: label, label });
  }
  return options;
};
